/**
 * productVisibility — frontend mirror of the role-based rule in
 * core/service/getPackagingProducts.go. Kept byte-for-byte consistent so the
 * "will be visible to:" preview on the product form matches what the backend
 * would return on the next list refresh.
 *
 * Rules (from the product spec):
 *   - The attributed user always sees their own product.
 *   - OWNER sees everything.
 *   - If the product is attributed to a MEMBER: that member sees it, plus any
 *     ANALYST / SUPERVISOR / ADMIN / OWNER (rank >= 2).
 *   - If the product is attributed to a non-MEMBER: MEMBERs don't see it;
 *     everyone else (ANALYST / SUPERVISOR / ADMIN / OWNER) does.
 */

const ROLE_RANK: Record<string, number> = {
    MEMBER: 1,
    ANALYST: 2,
    SUPERVISOR: 3,
    ADMIN: 4,
    OWNER: 5,
};

export function roleRank(role: string | undefined | null): number {
    if (!role) return 0;
    return ROLE_RANK[role.toUpperCase()] ?? 0;
}

export interface Viewer {
    id: string;
    role: string;
}

export interface AttributedProduct {
    attributedUserId: string;
    attributedRole: string;
}

export function canUserSeeProduct(viewer: Viewer, product: AttributedProduct): boolean {
    if (!viewer?.id || !product?.attributedUserId) return false;
    if (viewer.id === product.attributedUserId) return true;
    if (viewer.role?.toUpperCase() === "OWNER") return true;
    if (product.attributedRole?.toUpperCase() === "MEMBER") {
        return roleRank(viewer.role) >= 2;
    }
    return viewer.role?.toUpperCase() !== "MEMBER";
}

export interface OrgMember {
    id: string;
    email: string;
    role: string;
}

/**
 * Returns the subset of org members that would see a product attributed to
 * `attributed.userId` (assumed to have role `attributed.role`).
 */
export function computeVisibleMembers(
    orgMembers: OrgMember[],
    attributed: {userId: string; role: string},
): OrgMember[] {
    if (!attributed?.userId) return [];
    return (orgMembers ?? []).filter((m) =>
        canUserSeeProduct(
            {id: m.id, role: m.role},
            {attributedUserId: attributed.userId, attributedRole: attributed.role},
        ),
    );
}
